import { Box, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { KPIThresholdConfig } from "../../../models/ShiftReportConfig";

type BoundaryKey = keyof Omit<KPIThresholdConfig, "Name" | "MaxValue">;

const BOUNDARIES: { key: BoundaryKey }[] = [
	{ key: "Orange" },
	{ key: "Yellow" },
	{ key: "GreenSoft" },
	{ key: "GreenNormal" },
	{ key: "GreenHard" },
];

const SEGMENTS: {
	fromKey: BoundaryKey | null;
	toKey: BoundaryKey | null;
	color: string;
}[] = [
	{ fromKey: null, toKey: "Orange", color: "#F44336" },
	{ fromKey: "Orange", toKey: "Yellow", color: "#FF9800" },
	{ fromKey: "Yellow", toKey: "GreenSoft", color: "#FFC107" },
	{ fromKey: "GreenSoft", toKey: "GreenNormal", color: "#8BC34A" },
	{ fromKey: "GreenNormal", toKey: "GreenHard", color: "#4CAF50" },
	{ fromKey: "GreenHard", toKey: null, color: "#2E7D32" },
];

type Props = {
	thresholds: KPIThresholdConfig[];
	onChange: (name: string, zone: string, value: number) => void;
};

const MaxValueInput = ({
	value,
	min,
	onChange,
}: {
	value: number;
	min: number;
	onChange: (value: number) => void;
}) => {
	const [draft, setDraft] = useState(String(value));

	useEffect(() => {
		setDraft(String(value));
	}, [value]);

	return (
		<TextField
			type="number"
			size="small"
			value={draft}
			onChange={(e) => {
				const str = e.target.value;
				setDraft(str);
				if (str === "") return;
				const parsed = Number(str);
				if (!Number.isFinite(parsed)) return;
				onChange(Math.max(min, parsed));
			}}
			onBlur={() => {
				if (draft === "" || !Number.isFinite(Number(draft))) {
					setDraft(String(value));
				}
			}}
			InputProps={{ inputProps: { min, step: 1 } }}
			sx={{ width: 90 }}
		/>
	);
};

const TimeBar = ({
	threshold,
	onChange,
}: {
	threshold: KPIThresholdConfig;
	onChange: (name: string, zone: string, value: number) => void;
}) => {
	const barRef = useRef<HTMLDivElement>(null);
	const [dragging, setDragging] = useState<string | null>(null);
	const max = Math.max(1, threshold.MaxValue);

	const getHoursFromMouse = useCallback(
		(clientX: number): number => {
			if (!barRef.current) return 0;
			const rect = barRef.current.getBoundingClientRect();
			const pct = (clientX - rect.left) / rect.width;
			const raw = pct * max;
			return Math.max(0, Math.min(max, Math.round(raw * 2) / 2));
		},
		[max],
	);

	const getClampedValue = useCallback(
		(boundaryKey: BoundaryKey, rawValue: number): number => {
			const idx = BOUNDARIES.findIndex((b) => b.key === boundaryKey);
			const lower = idx > 0 ? threshold[BOUNDARIES[idx - 1].key] : 0;
			const upper =
				idx < BOUNDARIES.length - 1 ? threshold[BOUNDARIES[idx + 1].key] : max;
			return Math.max(lower, Math.min(upper, rawValue));
		},
		[threshold, max],
	);

	const handleMouseDown = useCallback(
		(boundaryKey: BoundaryKey) => (e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			setDragging(boundaryKey);

			const handleMouseMove = (moveEvent: MouseEvent) => {
				const raw = getHoursFromMouse(moveEvent.clientX);
				const clamped = getClampedValue(boundaryKey, raw);
				onChange(threshold.Name, boundaryKey, clamped);
			};

			const handleMouseUp = () => {
				setDragging(null);
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
			};

			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
		},
		[threshold.Name, onChange, getHoursFromMouse, getClampedValue],
	);

	const pctOf = (hours: number) => (hours / max) * 100;

	return (
		<Box sx={{ mb: 3 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
				<Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#555" }}>
					{threshold.Name}
				</Typography>
				<Box sx={{ flex: 1 }} />
				<Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
					Max Hours:
				</Typography>
				<MaxValueInput
					value={threshold.MaxValue}
					min={threshold.GreenHard}
					onChange={(v) => onChange(threshold.Name, "MaxValue", v)}
				/>
			</Box>
			<Box
				ref={barRef}
				sx={{
					position: "relative",
					height: 32,
					borderRadius: 1,
					overflow: "visible",
					border: "1px solid #ddd",
					display: "flex",
					userSelect: "none",
				}}
			>
				{SEGMENTS.map((seg) => {
					const fromHours = seg.fromKey ? threshold[seg.fromKey] : 0;
					const toHours = seg.toKey ? threshold[seg.toKey] : max;
					const width = Math.max(pctOf(toHours) - pctOf(fromHours), 0);
					if (width === 0) return null;
					return (
						<Box
							key={seg.color}
							sx={{
								width: `${width}%`,
								backgroundColor: seg.color,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
								transition: dragging ? "none" : "width 0.2s ease",
							}}
						>
							<Typography
								sx={{
									fontSize: "0.8rem",
									color: "#fff",
									fontWeight: 600,
									textShadow: "0 0 2px rgba(0,0,0,0.4)",
									whiteSpace: "nowrap",
									pointerEvents: "none",
								}}
							>
								{fromHours}h - {toHours}h
							</Typography>
						</Box>
					);
				})}

				{BOUNDARIES.map((b) => {
					const hours = threshold[b.key];
					return (
						<Box
							key={b.key}
							onMouseDown={handleMouseDown(b.key)}
							sx={{
								position: "absolute",
								left: `${pctOf(hours)}%`,
								top: -4,
								bottom: -4,
								width: 14,
								marginLeft: "-7px",
								cursor: "ew-resize",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								zIndex: dragging === b.key ? 10 : 5,
								"&:hover > div, &:active > div": {
									transform: "scale(1.3)",
								},
							}}
						>
							<Box
								sx={{
									width: 8,
									height: 38,
									borderRadius: 1,
									backgroundColor: dragging === b.key ? "#333" : "#666",
									border: "1px solid #fff",
									boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
									transition: "transform 0.1s, background-color 0.1s",
								}}
							/>
						</Box>
					);
				})}
			</Box>
			<Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.25 }}>
				<Typography sx={{ fontSize: "0.6rem", color: "#888" }}>0h</Typography>
				<Typography sx={{ fontSize: "0.6rem", color: "#888" }}>
					{max}h
				</Typography>
			</Box>
		</Box>
	);
};

export const TimeThresholdSlider = ({ thresholds, onChange }: Props) => {
	return (
		<Box>
			{thresholds.map((t) => (
				<TimeBar key={t.Name} threshold={t} onChange={onChange} />
			))}
		</Box>
	);
};
